/**
 * CampaignsPage — Proactive WATI template messaging (Iter 136, Feb 2026).
 *
 * Super-admin-only. Three tabs:
 *   1. Campaigns — list / create / enable / run-now
 *   2. Segments — preview which customers would be targeted right now
 *   3. Results — sent / replied / orders recovered ₹
 *
 * Per Yash's scope (Feb 1 2026):
 *   - Only "abandoned cart" v1 segments (payment_link_stalled +
 *     enquiry_no_reply toggle).
 *   - Schedule modes: "anytime" or "hours window in IST".
 *   - No marketing → no opt-out UI.
 */

import { useEffect, useState } from "react";
import { campaignsApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Megaphone, Play, Pause, Trash2, RefreshCw, Eye, Plus, Loader2,
  Clock, Filter, CheckCircle, XCircle, IndianRupee, MessageCircle, TrendingUp,
} from "lucide-react";

const SEGMENT_OPTIONS = [
  {
    key: "payment_link_stalled",
    label: "Stalled payment link",
    desc: "Customer has a Razorpay link that's still pending after N hours",
  },
  {
    key: "enquiry_no_reply",
    label: "Product enquiry — no reply",
    desc: "Customer asked about ≥ 2 products but never bought (Phase 1.5)",
  },
];

function StatusPill({ status }) {
  const map = {
    sent: { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
    failed: { color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
    queued: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
    preview_skip: { color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
    skipped: { color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200" },
  };
  const s = map[status] || map.skipped;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${s.bg} ${s.color} ${s.border}`}
      data-testid={`campaign-msg-status-${status}`}
    >
      {status === "sent" && <CheckCircle size={10} />}
      {status === "failed" && <XCircle size={10} />}
      {status}
    </span>
  );
}

function CampaignForm({ initial, onSaved, onCancel }) {
  const [data, setData] = useState(() => initial || {
    name: "",
    description: "",
    segment_type: "payment_link_stalled",
    template_base_name: "resume_order_payment",
    segment_config: { stale_after_hours: 24, max_age_hours: 168, min_amount_inr: 500 },
    hours_window: { mode: "anytime", start_hour: 9, end_hour: 21 },
    batch_limit: 50,
    dedup_window_hours: 48,
    enabled: false,
  });
  const [saving, setSaving] = useState(false);
  // Iter 137 — autocomplete the template base name from registered WATI templates.
  // Falls back gracefully when the cache is empty (initial deploy before any
  // /api/wati/templates sync has run).
  const [registeredTemplates, setRegisteredTemplates] = useState([]);
  useEffect(() => {
    campaignsApi.registeredTemplates()
      .then((r) => setRegisteredTemplates(r.data?.templates || []))
      .catch(() => {});
  }, []);
  const selectedTemplate = registeredTemplates.find((t) => t.base === data.template_base_name);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setSeg = (k, v) =>
    setData((d) => ({ ...d, segment_config: { ...d.segment_config, [k]: v } }));
  const setWin = (k, v) =>
    setData((d) => ({ ...d, hours_window: { ...d.hours_window, [k]: v } }));

  const submit = async () => {
    if (!data.name?.trim()) {
      toast.error("Campaign name required");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await campaignsApi.update(initial.id, data);
        toast.success("Campaign updated");
      } else {
        await campaignsApi.create(data);
        toast.success("Campaign created");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[--border] rounded-2xl p-6 space-y-4" data-testid="campaign-form">
      <h3 className="text-lg font-bold text-[--text-primary]" style={{ fontFamily: "Outfit, sans-serif" }}>
        {initial?.id ? "Edit campaign" : "New campaign"}
      </h3>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider">Name</label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => set("name", e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-[--border] rounded-lg text-sm"
            placeholder="Abandoned cart — daily"
            data-testid="campaign-name-input"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider">Template (base name)</label>
          <input
            type="text"
            value={data.template_base_name}
            onChange={(e) => set("template_base_name", e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-[--border] rounded-lg text-sm font-mono"
            placeholder="resume_order_payment"
            list="registered-templates-list"
            data-testid="campaign-template-input"
          />
          <datalist id="registered-templates-list">
            {registeredTemplates.map((t) => (
              <option key={t.base} value={t.base}>
                {(t.languages || []).join(", ")} · {t.placeholders} placeholders · {t.category || "?"}
              </option>
            ))}
          </datalist>
          {selectedTemplate ? (
            <p className="text-[10px] text-emerald-700 mt-1 inline-flex items-center gap-1" data-testid="template-resolved">
              <CheckCircle size={10} />
              {(selectedTemplate.languages || []).join(", ").toUpperCase()} · {selectedTemplate.placeholders} placeholders · {selectedTemplate.category}
            </p>
          ) : registeredTemplates.length === 0 ? (
            <p className="text-[10px] text-amber-700 mt-1">
              No templates synced yet. Open Settings → Sync WATI templates first, or paste an exact approved name.
            </p>
          ) : (
            <p className="text-[10px] text-amber-700 mt-1" data-testid="template-not-found">
              ⚠ Not in registered list. Will fail at send if Meta hasn't approved this name.
            </p>
          )}
          <p className="text-[10px] text-[--text-muted] mt-1">
            We auto-pick <code>_en</code> / <code>_hi</code> based on the customer's language.
          </p>
        </div>
      </div>

      <div>
        <label className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider">Segment</label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          {SEGMENT_OPTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => set("segment_type", s.key)}
              className={`text-left p-3 rounded-lg border-2 transition-all ${
                data.segment_type === s.key
                  ? "border-[--accent] bg-[--accent-pale]"
                  : "border-[--border] bg-white hover:border-[--accent-soft]"
              }`}
              data-testid={`campaign-segment-${s.key}`}
            >
              <div className="text-sm font-bold text-[--text-primary]">{s.label}</div>
              <div className="text-[11px] text-[--text-muted]">{s.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {data.segment_type === "payment_link_stalled" && (
        <div className="grid grid-cols-3 gap-3 p-3 bg-[--bg-cream] rounded-xl">
          <div>
            <label className="text-[10px] font-bold text-[--text-muted] uppercase">Stale after (hrs)</label>
            <input
              type="number" min={12} max={72}
              value={data.segment_config?.stale_after_hours ?? 24}
              onChange={(e) => setSeg("stale_after_hours", Number(e.target.value))}
              className="w-full mt-1 px-3 py-1.5 border border-[--border] rounded-lg text-sm"
              data-testid="seg-stale-after"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[--text-muted] uppercase">Max age (hrs)</label>
            <input
              type="number" min={24} max={336}
              value={data.segment_config?.max_age_hours ?? 168}
              onChange={(e) => setSeg("max_age_hours", Number(e.target.value))}
              className="w-full mt-1 px-3 py-1.5 border border-[--border] rounded-lg text-sm"
              data-testid="seg-max-age"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[--text-muted] uppercase">Min amount ₹</label>
            <input
              type="number" min={0}
              value={data.segment_config?.min_amount_inr ?? 500}
              onChange={(e) => setSeg("min_amount_inr", Number(e.target.value))}
              className="w-full mt-1 px-3 py-1.5 border border-[--border] rounded-lg text-sm"
              data-testid="seg-min-amount"
            />
          </div>
        </div>
      )}

      {data.segment_type === "enquiry_no_reply" && (
        <div className="grid grid-cols-3 gap-3 p-3 bg-[--bg-cream] rounded-xl">
          <div>
            <label className="text-[10px] font-bold text-[--text-muted] uppercase">Min products</label>
            <input
              type="number" min={1} max={10}
              value={data.segment_config?.min_products ?? 2}
              onChange={(e) => setSeg("min_products", Number(e.target.value))}
              className="w-full mt-1 px-3 py-1.5 border border-[--border] rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[--text-muted] uppercase">Quiet ≥ hrs</label>
            <input
              type="number" min={12}
              value={data.segment_config?.stale_min_hours ?? 24}
              onChange={(e) => setSeg("stale_min_hours", Number(e.target.value))}
              className="w-full mt-1 px-3 py-1.5 border border-[--border] rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-[--text-muted] uppercase">Quiet ≤ hrs</label>
            <input
              type="number" min={24}
              value={data.segment_config?.stale_max_hours ?? 72}
              onChange={(e) => setSeg("stale_max_hours", Number(e.target.value))}
              className="w-full mt-1 px-3 py-1.5 border border-[--border] rounded-lg text-sm"
            />
          </div>
        </div>
      )}

      <div>
        <label className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider">When can this campaign send?</label>
        <div className="flex gap-2 mt-1 mb-2">
          <button type="button"
            onClick={() => setWin("mode", "anytime")}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-bold border-2 ${
              data.hours_window?.mode === "anytime"
                ? "border-[--accent] bg-[--accent-pale] text-[--accent]"
                : "border-[--border] bg-white text-[--text-secondary]"
            }`}
            data-testid="schedule-anytime-btn"
          >Run 24/7</button>
          <button type="button"
            onClick={() => setWin("mode", "hours")}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-bold border-2 ${
              data.hours_window?.mode === "hours"
                ? "border-[--accent] bg-[--accent-pale] text-[--accent]"
                : "border-[--border] bg-white text-[--text-secondary]"
            }`}
            data-testid="schedule-hours-btn"
          >Specific hours (IST)</button>
        </div>
        {data.hours_window?.mode === "hours" && (
          <div className="grid grid-cols-2 gap-3 p-3 bg-[--bg-cream] rounded-xl">
            <div>
              <label className="text-[10px] font-bold text-[--text-muted] uppercase">Start hour (IST)</label>
              <input type="number" min={0} max={23}
                value={data.hours_window?.start_hour ?? 9}
                onChange={(e) => setWin("start_hour", Number(e.target.value))}
                className="w-full mt-1 px-3 py-1.5 border border-[--border] rounded-lg text-sm"
                data-testid="schedule-start-hour"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold text-[--text-muted] uppercase">End hour (IST)</label>
              <input type="number" min={1} max={24}
                value={data.hours_window?.end_hour ?? 21}
                onChange={(e) => setWin("end_hour", Number(e.target.value))}
                className="w-full mt-1 px-3 py-1.5 border border-[--border] rounded-lg text-sm"
                data-testid="schedule-end-hour"
              />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] font-bold text-[--text-muted] uppercase">Batch / tick</label>
          <input type="number" min={1} max={500}
            value={data.batch_limit ?? 50}
            onChange={(e) => set("batch_limit", Number(e.target.value))}
            className="w-full mt-1 px-3 py-1.5 border border-[--border] rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold text-[--text-muted] uppercase">Dedup window (hrs)</label>
          <input type="number" min={24} max={168}
            value={data.dedup_window_hours ?? 48}
            onChange={(e) => set("dedup_window_hours", Number(e.target.value))}
            className="w-full mt-1 px-3 py-1.5 border border-[--border] rounded-lg text-sm"
          />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox"
              checked={!!data.enabled}
              onChange={(e) => set("enabled", e.target.checked)}
              className="w-4 h-4"
              data-testid="campaign-enabled-toggle"
            />
            <span className="text-sm font-bold text-[--text-primary]">Enabled</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-[--border]">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-bold border border-[--border] text-[--text-secondary] hover:bg-gray-50" data-testid="campaign-cancel-btn">Cancel</button>
        <button onClick={submit} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-bold bg-[--accent] text-white hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-2" data-testid="campaign-save-btn">
          {saving && <Loader2 size={14} className="animate-spin" />}
          {initial?.id ? "Save changes" : "Create campaign"}
        </button>
      </div>
    </div>
  );
}

function CampaignRow({ c, onChanged }) {
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);
  const [results, setResults] = useState(null);

  const loadResults = async () => {
    try {
      const r = await campaignsApi.results(c.id, 30);
      setResults(r.data);
    } catch {}
  };
  useEffect(() => {
    loadResults();
    // loadResults closes over c.id and is filter-derived; we only want
    // to refetch when the campaign id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id]);

  const toggleEnabled = async () => {
    setBusy("toggle");
    try {
      await campaignsApi.update(c.id, { enabled: !c.enabled });
      toast.success(c.enabled ? "Campaign paused" : "Campaign enabled");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setBusy(""); }
  };
  const runNow = async () => {
    setBusy("run");
    try {
      const r = await campaignsApi.runNow(c.id);
      const res = r.data?.result;
      toast.success(`Run complete — sent ${res?.sent || 0}, skipped ${res?.skipped || 0}${res?.reason ? ` (${res.reason})` : ""}`);
      onChanged?.();
      loadResults();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete campaign "${c.name}"? Past message logs are kept.`)) return;
    setBusy("delete");
    try {
      await campaignsApi.remove(c.id);
      toast.success("Campaign deleted");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(""); }
  };

  if (editing) {
    return <CampaignForm initial={c} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged?.(); }} />;
  }

  const win = c.hours_window?.mode === "hours"
    ? `${c.hours_window?.start_hour ?? 9}-${c.hours_window?.end_hour ?? 21} IST`
    : "Anytime";

  return (
    <div className="bg-white border border-[--border] rounded-2xl p-5 space-y-3" data-testid={`campaign-row-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-base font-bold text-[--text-primary] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{c.name}</h4>
            {c.enabled
              ? <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`campaign-${c.id}-status`}>ENABLED</span>
              : <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200" data-testid={`campaign-${c.id}-status`}>PAUSED</span>}
          </div>
          <p className="text-[11px] text-[--text-muted] mt-1">
            <span className="font-mono">{c.template_base_name}</span> · <Filter size={9} className="inline" /> {c.segment_type} · <Clock size={9} className="inline" /> {win} · batch {c.batch_limit}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={toggleEnabled} disabled={!!busy} className="btn-icon" title={c.enabled ? "Pause" : "Enable"} data-testid={`campaign-${c.id}-toggle`}>
            {c.enabled ? <Pause size={14} /> : <Play size={14} />}
          </button>
          <button onClick={runNow} disabled={!!busy} className="btn-icon" title="Run now" data-testid={`campaign-${c.id}-run`}>
            {busy === "run" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          <button onClick={() => setEditing(true)} disabled={!!busy} className="btn-icon" title="Edit" data-testid={`campaign-${c.id}-edit`}>
            <Eye size={14} />
          </button>
          <button onClick={remove} disabled={!!busy} className="btn-icon text-red-500" title="Delete" data-testid={`campaign-${c.id}-delete`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {results && (
        <div className="grid grid-cols-5 gap-2 text-center">
          <div className="p-2 bg-blue-50 rounded-lg">
            <div className="text-lg font-bold text-blue-700">{results.sent}</div>
            <div className="text-[9px] text-blue-500 uppercase font-bold">Sent (30d)</div>
          </div>
          <div className="p-2 bg-amber-50 rounded-lg">
            <div className="text-lg font-bold text-amber-700">{results.replied}</div>
            <div className="text-[9px] text-amber-600 uppercase font-bold">Replied</div>
          </div>
          <div className="p-2 bg-emerald-50 rounded-lg">
            <div className="text-lg font-bold text-emerald-700">{results.orders_recovered}</div>
            <div className="text-[9px] text-emerald-600 uppercase font-bold">Orders won</div>
          </div>
          <div className="p-2 bg-rose-50 rounded-lg">
            <div className="text-lg font-bold text-rose-700">₹{Math.round(results.revenue_recovered_inr || 0).toLocaleString("en-IN")}</div>
            <div className="text-[9px] text-rose-600 uppercase font-bold">Revenue ₹</div>
          </div>
          <div className="p-2 bg-indigo-50 rounded-lg">
            <div className="text-lg font-bold text-indigo-700">{results.conversion_rate}%</div>
            <div className="text-[9px] text-indigo-600 uppercase font-bold">CVR</div>
          </div>
        </div>
      )}
      {c.last_run_at && (
        <p className="text-[10px] text-[--text-muted]">
          Last run: {new Date(c.last_run_at).toLocaleString("en-IN")} · runs: {c.runs_count || 0}
        </p>
      )}
    </div>
  );
}

function SegmentPreview() {
  const [segType, setSegType] = useState("payment_link_stalled");
  const [staleHrs, setStaleHrs] = useState(24);
  const [minAmt, setMinAmt] = useState(500);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const params = { segment_type: segType, limit: 50 };
      if (segType === "payment_link_stalled") {
        params.stale_after_hours = staleHrs;
        params.min_amount_inr = minAmt;
      }
      const r = await campaignsApi.previewSegment(params);
      setMembers(r.data?.members || []);
      toast.success(`Preview: ${r.data?.count || 0} customer(s) match`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-[--border] rounded-2xl p-5 space-y-3">
        <h3 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Live segment preview</h3>
        <p className="text-xs text-[--text-muted]">Read-only — see exactly who would receive a template right now.</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] font-bold text-[--text-muted] uppercase">Segment</label>
            <select value={segType} onChange={(e) => setSegType(e.target.value)} className="w-full mt-1 px-3 py-2 border border-[--border] rounded-lg text-sm" data-testid="preview-segment-select">
              {SEGMENT_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          {segType === "payment_link_stalled" && (
            <>
              <div>
                <label className="text-[10px] font-bold text-[--text-muted] uppercase">Stale ≥ hrs</label>
                <input type="number" min={1} value={staleHrs} onChange={(e) => setStaleHrs(Number(e.target.value))} className="w-full mt-1 px-3 py-2 border border-[--border] rounded-lg text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-[--text-muted] uppercase">Min ₹</label>
                <input type="number" min={0} value={minAmt} onChange={(e) => setMinAmt(Number(e.target.value))} className="w-full mt-1 px-3 py-2 border border-[--border] rounded-lg text-sm" />
              </div>
            </>
          )}
        </div>
        <button onClick={run} disabled={loading} className="px-4 py-2 rounded-lg bg-[--accent] text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60" data-testid="preview-run-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview
        </button>
      </div>

      <div className="bg-white border border-[--border] rounded-2xl overflow-hidden">
        {members.length === 0 ? (
          <div className="p-8 text-center text-[--text-muted] text-sm" data-testid="preview-empty">
            <Megaphone size={28} className="mx-auto mb-2 opacity-40" />
            No customers match this segment right now. Try lowering "stale ≥ hrs" or "min ₹".
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[--bg-cream]">
              <tr>
                <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Name</th>
                <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Phone</th>
                <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Item</th>
                <th className="text-right text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Amount</th>
                <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Stalled since</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.lead_id} className="border-t border-[--border]" data-testid={`preview-row-${m.lead_id}`}>
                  <td className="px-3 py-2 font-bold text-[--text-primary]">{m.name}</td>
                  <td className="px-3 py-2 text-[--text-secondary] font-mono">{m.phone}</td>
                  <td className="px-3 py-2 text-[--text-secondary] truncate max-w-[280px]">{m.description}</td>
                  <td className="px-3 py-2 text-right font-bold text-[--text-primary]">
                    {m.amount ? <><IndianRupee size={12} className="inline" />{Math.round(m.amount).toLocaleString("en-IN")}</> : "—"}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-[--text-muted]">{m.stalled_since ? new Date(m.stalled_since).toLocaleString("en-IN") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ResultsTab({ campaigns }) {
  const [allMessages, setAllMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    campaignsApi.messages(undefined, 200)
      .then((r) => setAllMessages(r.data?.messages || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totals = campaigns.reduce(
    (acc, c) => {
      // We don't have totals on c — placeholder, use allMessages instead
      return acc;
    },
    { sent: 0, replied: 0, orders: 0, revenue: 0 }
  );

  const sentCount = allMessages.filter((m) => m.status === "sent" || m.status === "preview_skip").length;
  const failedCount = allMessages.filter((m) => m.status === "failed").length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        <StatCard icon={MessageCircle} label="Total sent" value={sentCount} color="blue" />
        <StatCard icon={XCircle} label="Failed" value={failedCount} color="red" />
        <StatCard icon={TrendingUp} label="Active campaigns" value={campaigns.filter((c) => c.enabled).length} color="emerald" />
        <StatCard icon={Megaphone} label="Total campaigns" value={campaigns.length} color="amber" />
      </div>

      <div className="bg-white border border-[--border] rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[--border]">
          <h3 className="text-base font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Recent campaign messages</h3>
          <p className="text-[11px] text-[--text-muted]">Latest 200 sends across all campaigns.</p>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Loader2 size={20} className="animate-spin inline text-[--text-muted]" /></div>
        ) : allMessages.length === 0 ? (
          <div className="p-8 text-center text-[--text-muted] text-sm">No campaign messages yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[--bg-cream]">
              <tr>
                <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">When</th>
                <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Campaign</th>
                <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Phone</th>
                <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Template</th>
                <th className="text-right text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Amount</th>
                <th className="text-left text-[10px] uppercase font-bold text-[--text-muted] px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {allMessages.map((m) => (
                <tr key={m.id} className="border-t border-[--border]" data-testid={`results-row-${m.id}`}>
                  <td className="px-3 py-2 text-[11px] text-[--text-muted]">{m.sent_at ? new Date(m.sent_at).toLocaleString("en-IN") : "—"}</td>
                  <td className="px-3 py-2 text-[--text-primary] font-bold">{m.campaign_name || m.campaign_id?.slice(0, 8)}</td>
                  <td className="px-3 py-2 font-mono text-[--text-secondary]">{m.phone}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-[--text-secondary]">{m.template_name}</td>
                  <td className="px-3 py-2 text-right text-[--text-secondary]">
                    {m.amount_inr ? <>₹{Math.round(m.amount_inr).toLocaleString("en-IN")}</> : "—"}
                  </td>
                  <td className="px-3 py-2"><StatusPill status={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const palette = {
    blue: { bg: "bg-blue-50", text: "text-blue-700" },
    red: { bg: "bg-red-50", text: "text-red-700" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-700" },
    amber: { bg: "bg-amber-50", text: "text-amber-700" },
  }[color] || { bg: "bg-gray-50", text: "text-gray-700" };
  return (
    <div className={`${palette.bg} rounded-2xl p-4 border border-current/10`}>
      <Icon size={18} className={palette.text} />
      <div className={`text-3xl font-extrabold mt-1 ${palette.text}`} style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
      <div className={`text-[10px] uppercase font-bold ${palette.text} opacity-70`}>{label}</div>
    </div>
  );
}

export default function CampaignsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("campaigns");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const isSuper = user?.role === "super_admin";

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const r = await campaignsApi.list();
      setCampaigns(r.data?.campaigns || []);
    } catch (e) {
      toast.error("Failed to load campaigns");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchCampaigns(); }, []);

  if (!isSuper) {
    return (
      <div className="p-6" data-testid="campaigns-no-access">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 max-w-2xl">
          <h2 className="text-lg font-bold text-amber-800">Super-admin only</h2>
          <p className="text-sm text-amber-700 mt-2">
            Proactive campaigns are restricted to super-admins because each send costs ₹.
            Ask Yash or Rithvik for access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="campaigns-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-[--text-primary]" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Megaphone size={26} className="inline mr-2 -mt-1 text-[--accent]" />
            Proactive campaigns
          </h1>
          <p className="text-sm text-[--text-muted] mt-1">
            Re-engage customers whose 24-h WhatsApp window has expired — using pre-approved WATI templates.
          </p>
        </div>
        {tab === "campaigns" && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 rounded-full bg-[--accent] text-white text-sm font-bold inline-flex items-center gap-2 hover:opacity-90"
            data-testid="campaign-new-btn"
          >
            <Plus size={14} /> New campaign
          </button>
        )}
      </div>

      <div className="flex gap-1 mb-6 bg-white border border-[--border] rounded-full p-1 inline-flex" data-testid="campaigns-tabs">
        {[
          { k: "campaigns", l: "Campaigns" },
          { k: "preview", l: "Preview segment" },
          { k: "results", l: "Results" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => { setTab(t.k); setShowForm(false); }}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
              tab === t.k ? "bg-[--accent] text-white" : "text-[--text-secondary] hover:bg-gray-50"
            }`}
            data-testid={`campaigns-tab-${t.k}`}
          >{t.l}</button>
        ))}
      </div>

      {tab === "campaigns" && (
        <div className="space-y-4">
          {showForm && (
            <CampaignForm
              onCancel={() => setShowForm(false)}
              onSaved={() => { setShowForm(false); fetchCampaigns(); }}
            />
          )}
          {loading ? (
            <div className="text-center py-12"><Loader2 size={20} className="animate-spin inline text-[--text-muted]" /></div>
          ) : campaigns.length === 0 && !showForm ? (
            <div className="bg-white border border-dashed border-[--border] rounded-2xl p-12 text-center" data-testid="campaigns-empty">
              <Megaphone size={32} className="mx-auto mb-3 text-[--text-muted] opacity-50" />
              <p className="text-sm text-[--text-secondary] font-bold">No campaigns yet</p>
              <p className="text-xs text-[--text-muted] mt-1 mb-4">Create your first abandoned-cart campaign to re-engage customers.</p>
              <button onClick={() => setShowForm(true)} className="px-4 py-2 rounded-full bg-[--accent] text-white text-sm font-bold" data-testid="campaigns-empty-cta">
                <Plus size={14} className="inline mr-1" /> New campaign
              </button>
            </div>
          ) : (
            campaigns.map((c) => (
              <CampaignRow key={c.id} c={c} onChanged={fetchCampaigns} />
            ))
          )}
        </div>
      )}

      {tab === "preview" && <SegmentPreview />}
      {tab === "results" && <ResultsTab campaigns={campaigns} />}
    </div>
  );
}
